<template>
  <StepLayout
    :title="$t('components.passport.information.label')"
    :showBack="!isDirty"
    class="p-4"
    @back="handleBack"
  >
    <template #button="{ isMobile }">
      <Transition name="fade-slow" mode="out-in" appear>
        <div v-if="isDirty" class="flex items-center">
          <Badge
            :label="$t('components.badges.edit_mode')"
            type="light"
            color="brand"
            :size="isMobile ? 'md' : 'sm'"
          />
        </div>
      </Transition>
    </template>
    <div
      class="flex flex-col p-4 ltr:pr-2 rtl:pl-2 gap-3 bg-primary border border-secondary rounded-sm"
    >
      <Properties
        :properties="properties"
        :max="properties.length"
        :buildingId="item.tenant.id"
        size="sm"
        :disabled="!canUpdate"
        @goToStep="open[$event] = true"
        @update:form="
          ({ property, value }) => {
            form[property] = value;
          }
        "
      />
    </div>
    <CategorySelectModal
      v-model:open="open.category"
      :title="$t('components.properties.category')"
      :initialState="item[PropertyType.CATEGORY]"
      :buildingIds="[item.tenant.id]"
      @update="form[PropertyType.CATEGORY] = $event"
    />
    <DateSelectModal
      v-model:open="open[PropertyType.PURCHASE_DATE]"
      :title="$t('components.properties.purchased_at')"
      :initialState="item[PropertyType.PURCHASE_DATE]"
      @update="form[PropertyType.PURCHASE_DATE] = $event"
    />
  </StepLayout>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import { Properties } from "@/Pages/Assets/Partials/Create";
import { Badge } from "@/SharedComponents/Badges";
import {
  CategorySelectModal,
  DateSelectModal,
} from "@/SharedComponents/Modals";
import { StepLayout } from "@/SharedComponents/Passport";
import { useProperties, PropertyType } from "@/Composables/useProperties";

const emit = defineEmits(["back", "update"]);

const props = defineProps({
  item: { type: Object, required: true },
  type: { type: String, required: true },
  canUpdate: { type: Boolean, required: false },
});

const open = ref({
  [PropertyType.CATEGORY]: false,
  [PropertyType.PURCHASE_DATE]: false,
});

const form = ref({});

const { properties } = useProperties(props.type, form);

onMounted(() => {
  form.value = useForm(
    properties.value.reduce((formData, property) => {
      formData[property.key] = props.item[property.key];
      return formData;
    }, {})
  );
});

const isDirty = computed(() => form.value.isDirty);

function handleBack() {
  cancel();
  emit("back");
}

function cancel() {
  form.value.reset();
}

function submit() {
  emit("update", {
    data: form.value.data(),
    callback: (success) => {
      success && form.value.defaults();
      form.value.reset();
    },
  });
}

defineExpose({
  editing: isDirty,
  isDirty,
  cancel,
  submit,
});
</script>
